import { gameRules } from './gameRules';

// Klasyczne zasady (adaptacja) zgodnie z wymaganiami użytkownika:
// - Pojedyncze: 1 = 10 pkt, 5 = 5 pkt, inne = 0
// - Sety (>=3):
//      * wartość != 1: 3 of a kind => (wartość * 10)
//      * wartość == 1: trzy jedynki => 100
//      * 4 of a kind => podwaja wartość 3-of-a-kind
//      * 5 of a kind => ponownie podwaja (w sumie x4 bazowej 3-of-a-kind)
// - W secie kości nie liczą się jako pojedyncze
// - Zwracamy dodatkowo ile kości wniosło punkty (scoringDiceCount) – przydatne do walidacji ≥3 kości przed bankowaniem (<900 pkt)

export type ScoreBreakdownEntry =
  | { kind: 'straight'; start: 1 | 2; end: 5 | 6; count: 5; points: number }
  | { kind: 'set'; value: number; count: number; points: number }
  | { kind: 'single'; value: number; count: number; points: number };

export interface ScoreComputation {
  basePoints: number;          // suma punktów za wybraną/ocenianą konfigurację
  scoringDiceCount: number;    // ile kości faktycznie dało punkty
  breakdown: string[];         // legacy sformatowane linie
  breakdownDetails: ScoreBreakdownEntry[]; // strukturalne dane dla UI
}

/**
 * Computes score breakdown for a given dice set using game rules.
 *
 * @example
 * // Strit (1-5 lub 2-6): 150 pkt
 * computeScore([1, 2, 3, 4, 5]).basePoints // 150
 *
 * @example
 * // Sety: 3×1=100, 3×6=60 (x2 za 4-of-a-kind), 4×5=40 (pojedyncze)
 * computeScore([1, 1, 1, 6, 6, 6, 5]).basePoints // 260
 *
 * @example
 * // Pojedyncze: 1=10 pkt, 5=5 pkt
 * computeScore([1, 2, 3, 5]).basePoints // 15
 *
 * @example
 * // Brak punktów
 * computeScore([2, 3, 4]).basePoints // 0
 */
export function computeScore(dice: number[]): ScoreComputation {
  if (!dice || dice.length === 0) {
    return { basePoints: 0, scoringDiceCount: 0, breakdown: ['brak kości'], breakdownDetails: [] };
  }

  const diceCount = gameRules.dice.count;
  const diceFaces = gameRules.dice.faces;

  // Strit (5 kolejnych): 1-5 lub 2-6, tylko gdy mamy dokładnie 5 kości.
  if (dice.length === diceCount) {
    const uniq = Array.from(new Set(dice)).sort((a, b) => a - b);
    const isStraight = uniq.length === diceCount && (
      (uniq[0] === 1 && uniq[diceCount - 1] === diceCount)
      || (uniq[0] === 2 && uniq[diceCount - 1] === diceFaces)
    );
    if (isStraight) {
      const points = 150;
      const detail: ScoreBreakdownEntry = {
        kind: 'straight',
        start: uniq[0] as 1 | 2,
        end: uniq[diceCount - 1] as 5 | 6,
        count: diceCount as 5,
        points,
      };
      return {
        basePoints: points,
        scoringDiceCount: diceCount,
        breakdown: [`Strit ${uniq[0]}-${uniq[diceCount - 1]}: ${points}`, `Razem: ${points}`],
        breakdownDetails: [detail],
      };
    }
  }

  const counts: Record<number, number> = {};
  dice.forEach(v => { counts[v] = (counts[v] || 0) + 1; });

  let total = 0;
  let scoringDice = 0;
  const breakdown: string[] = [];
  const breakdownDetails: ScoreBreakdownEntry[] = [];

  // Sety
  Object.entries(counts).forEach(([valStr, cnt]) => {
    const val = Number(valStr);
    if (cnt >= 3) {
      const baseTriple = (val === 1) ? 100 : val * 10; // 3-of-a-kind bazowe
      let mult = 1; // dla trójki
      if (cnt === 4) mult = 2; // podwójne
      else if (cnt === diceCount) mult = 4; // dodatkowe podwojenie (razem x4)
      const setPoints = baseTriple * mult;
      total += setPoints;
      scoringDice += cnt;
      breakdown.push(`${cnt}×${val}: ${setPoints}`);
      breakdownDetails.push({ kind: 'set', value: val, count: cnt, points: setPoints });
    }
  });

  // Pojedyncze (tylko te które nie są włączone do setu)
  Object.entries(counts).forEach(([valStr, cnt]) => {
    const val = Number(valStr);
    const inSet = cnt >= 3; // wszystkie kości tej wartości już zaliczone w secie
    if (inSet) return;
    if (val === 1 || val === 5) {
      const singleValue = val === 1 ? 10 : 5;
      const pts = cnt * singleValue;
      total += pts;
      scoringDice += cnt;
      breakdown.push(`${cnt}×${val} (pojedyncze): ${pts}`);
      breakdownDetails.push({ kind: 'single', value: val, count: cnt, points: pts });
    }
  });

  if (breakdown.length === 0) breakdown.push('Brak punktujących kości');
  breakdown.push(`Razem: ${total}`);
  return { basePoints: total, scoringDiceCount: scoringDice, breakdown, breakdownDetails };
}
